import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Tag, Plus, X, Star, Crown, AlertTriangle, DollarSign, Globe, Award, MessageCircle } from 'lucide-react';

/* ──────────────────────────────────────────────────────────────────────────
 * ContactTagPicker (Phase D)
 *
 * Smart tag picker for the contact header. Renders existing tags as colored
 * chips and lets the user add preset tags (one click) or custom tags.
 *
 * Storage: tags column is TEXT, comma-separated. We parse to array for
 * display and edit, then join back to comma-separated on save.
 *
 * Preset taxonomy is intentionally lightweight — these aren't enums, just
 * one-click suggestions. Users can still add anything custom.
 *
 * Props:
 *   contact     — the contact row (must have .id and .tags)
 *   onPatched   — fires after successful save, used by parent to refresh
 * ────────────────────────────────────────────────────────────────────────── */

// Each preset has: id (the actual tag string), label, icon, color
// Color is an accent for the chip when this preset is applied.
const PRESET_TAGS = [
  { id: 'VIP',                label: 'VIP',                icon: Crown,         color: '#A855F7' },
  { id: 'High Revenue',       label: 'High Revenue',       icon: DollarSign,    color: '#10B981' },
  { id: 'Difficult Patient',  label: 'Difficult Patient',  icon: AlertTriangle, color: '#DC2626' },
  { id: 'Financing Risk',     label: 'Financing Risk',     icon: AlertTriangle, color: '#F59E0B' },
  { id: 'International',      label: 'International',      icon: Globe,         color: '#0EA5E9' },
  { id: 'Athlete',            label: 'Athlete',            icon: Award,         color: '#16A34A' },
  { id: 'Celebrity',          label: 'Celebrity',          icon: Star,          color: '#EC4899' },
  { id: 'Referral Partner',   label: 'Referral Partner',   icon: Star,          color: '#0EA5E9' },
  { id: 'Spanish Speaking',   label: 'Spanish Speaking',   icon: MessageCircle, color: '#0EA5E9' },
  { id: 'Needs Translator',   label: 'Needs Translator',   icon: MessageCircle, color: '#F59E0B' },
];

// Build a lookup so we can colorize chips that match a preset.
const PRESET_LOOKUP = Object.fromEntries(PRESET_TAGS.map(p => [p.id.toLowerCase(), p]));

// Parse tags TEXT → array; strip empties + dedupe (case-insensitive)
function parseTags(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(s => String(s).trim()).filter(Boolean);
  return String(raw)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function uniqCI(arr) {
  const seen = new Set();
  const out = [];
  for (const t of arr) {
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

export default function ContactTagPicker({ contact, onPatched }) {
  // ⚠ EDITS GO TO zgx_flags, NOT tags (migration 174, 2026-09-05).
  //
  // crm_contacts.tags is a MIRROR of GHL's tag list: the 15-minute reconcile
  // writes `tags = COALESCE(NEW, existing)` (scripts/ghl_daily_sync.js:371,480
  // — the rule is stated in the comment at :363,:472), so anything a rep set
  // there was erased the next time GHL sent tags for that contact. The sync has
  // no statement touching zgx_flags, so flags written here cannot be overwritten
  // by it — the guarantee is structural, not a policy anyone has to remember.
  //
  // GHL's own tags stay visible below as read-only chips so nothing disappears
  // from the rep's view; they are simply not editable from here, because
  // editing them would not survive.
  const [tags, setTags] = useState(() => parseTags(contact?.zgx_flags));
  const ghlTags = parseTags(contact?.tags);
  const [showPicker, setShowPicker] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const pickerRef = useRef(null);
  const customInputRef = useRef(null);

  // Re-sync local tags when contact reloads
  useEffect(() => {
    setTags(parseTags(contact?.zgx_flags));
  }, [contact?.id, contact?.updated_at, contact?.zgx_flags]);

  // Close picker on outside click
  useEffect(() => {
    if (!showPicker) return;
    function onDocClick(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
        setCustomInput('');
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showPicker]);

  // Focus the custom input when picker opens
  useEffect(() => {
    if (showPicker && customInputRef.current) customInputRef.current.focus();
  }, [showPicker]);

  // Persist tags array back to TEXT column (comma-separated)
  async function saveTags(nextTags) {
    setSaving(true); setError(null);
    try {
      const csv = nextTags.length === 0 ? null : nextTags.join(', ');
      const r = await window.zignagenixAPI.patchContactFields(contact.id, { zgx_flags: csv });
      if (!r || r.success === false) {
        setError(r?.error || 'save failed');
        return false;
      }
      if (onPatched) onPatched();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function addTag(t) {
    const trimmed = String(t || '').trim();
    if (!trimmed) return;
    if (tags.some(existing => existing.toLowerCase() === trimmed.toLowerCase())) {
      // Already has it — silently no-op
      return;
    }
    const next = uniqCI([...tags, trimmed]);
    setTags(next);
    await saveTags(next);
  }

  async function removeTag(t) {
    const next = tags.filter(x => x.toLowerCase() !== t.toLowerCase());
    setTags(next);
    await saveTags(next);
  }

  function onCustomKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(customInput).then(() => setCustomInput(''));
    } else if (e.key === 'Escape') {
      setShowPicker(false);
      setCustomInput('');
    }
  }

  // Preset suggestions still available (those not already on this contact)
  const availablePresets = useMemo(
    () => PRESET_TAGS.filter(p => !tags.some(t => t.toLowerCase() === p.id.toLowerCase())),
    [tags]
  );

  if (!contact) return null;

  const TagChip = ({ value, onRemove }) => {
    const preset = PRESET_LOOKUP[value.toLowerCase()];
    const Icon = preset?.icon || Tag;
    const color = preset?.color || '#475569';
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 8px', borderRadius: 12,
        background: color + '15',
        border: `1px solid ${color}55`,
        color, fontSize: 11, fontWeight: 600,
      }}>
        <Icon size={11} />
        {value}
        {onRemove && (
          <button onClick={onRemove} disabled={saving}
            title="Remove tag"
            style={{
              padding: 0, marginLeft: 2, border: 'none', background: 'none',
              cursor: 'pointer', color, opacity: 0.7, display: 'flex',
            }}>
            <X size={10} />
          </button>
        )}
      </span>
    );
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', position: 'relative' }}>
      {tags.map(t => (
        <TagChip key={t} value={t} onRemove={() => removeTag(t)} />
      ))}

      {/* GHL's own tags, read-only (migration 174). Shown so nothing vanishes
          from the rep's view when flags moved to zgx_flags, but not editable:
          crm_contacts.tags mirrors GHL and the next reconcile would overwrite
          any edit made here. No onRemove, so the chip has no affordance it
          cannot honour. */}
      {ghlTags
        .filter(t => !tags.some(z => z.toLowerCase() === t.toLowerCase()))
        .map(t => (
          <span key={`ghl-${t}`} title="From GoHighLevel — managed there, not editable here"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 8px', borderRadius: 12,
              background: '#F1F5F9', border: '1px dashed #94A3B8',
              color: '#475569', fontSize: 11, fontWeight: 600,
            }}>
            <Tag size={11} />
            {t}
          </span>
        ))}

      <button
        onClick={() => setShowPicker(v => !v)}
        disabled={saving}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 8px', borderRadius: 12,
          background: '#F1F5F9', border: '1px dashed #94A3B8',
          color: '#475569', fontSize: 11, fontWeight: 600, cursor: 'pointer',
        }}
        title="Add tag"
      >
        <Plus size={11} /> {tags.length === 0 ? 'Add tag' : 'Add'}
      </button>

      {error && (
        <span style={{ fontSize: 10, color: '#DC2626' }}>✗ {error}</span>
      )}
      {saving && (
        <span style={{ fontSize: 10, color: '#94A3B8' }}>saving…</span>
      )}

      {showPicker && (
        <div ref={pickerRef} style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 6,
          zIndex: 50, minWidth: 280, maxWidth: 360,
          background: '#FFFFFF', border: '1px solid #E2E8F0',
          borderRadius: 10, padding: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748B',
                        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
            Quick add
          </div>
          {availablePresets.length === 0 ? (
            <div style={{ fontSize: 11, color: '#94A3B8', padding: '4px 0' }}>
              All presets applied — type a custom tag below.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {availablePresets.map(p => {
                const Icon = p.icon;
                return (
                  <button key={p.id} onClick={() => addTag(p.id)} disabled={saving}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '4px 8px', borderRadius: 12,
                      background: p.color + '12',
                      border: `1px solid ${p.color}55`,
                      color: p.color, fontSize: 11, fontWeight: 600,
                      cursor: 'pointer',
                    }}>
                    <Icon size={11} /> {p.label}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748B',
                        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
            Custom tag
          </div>
          <input
            ref={customInputRef}
            type="text"
            value={customInput}
            onChange={e => setCustomInput(e.target.value)}
            onKeyDown={onCustomKeyDown}
            placeholder="Type tag and press Enter"
            style={{
              width: '100%', padding: '5px 8px',
              border: '1px solid #CBD5E1', borderRadius: 6,
              fontSize: 12, outline: 'none',
            }}
          />
        </div>
      )}
    </div>
  );
}
